import { buildBodyParentMap } from "../utils/bodyHierarchy";
import { logger } from "../utils/logger";
import type { BodyUUID, Joint, JointUUID, Quaternion, Vector3 } from "../types/scene";
import type { IkChain, IkLink, SceneLike } from "./types";

const scalarJointKinds = new Set(["hinge", "slide"]);

type ScalarIkJoint = Extract<Joint, { kind: "hinge" | "slide" }>;

const isScalarIkJoint = (joint: Joint | undefined): joint is ScalarIkJoint =>
  !!joint && scalarJointKinds.has(joint.kind);

const toVector3Tuple = (value: unknown, fallback: Vector3): Vector3 =>
  Array.isArray(value) && value.length >= 3
    ? ([value[0], value[1], value[2]] as Vector3)
    : fallback;

const toQuaternionTuple = (value: unknown, fallback: Quaternion): Quaternion =>
  Array.isArray(value) && value.length >= 4
    ? ([value[0], value[1], value[2], value[3]] as Quaternion)
    : fallback;

let lastIkChainBuildFailureReason = "";

export function getLastIkChainBuildFailureReason(): string {
  return lastIkChainBuildFailureReason;
}

function failIkChainBuild(reason: string): null {
  lastIkChainBuildFailureReason = reason;
  logger.warn(`[ik] ${reason}`);
  return null;
}

function collectPath(
  parentMap: Record<BodyUUID, BodyUUID | null>,
  endEffectorBody: BodyUUID,
  rootBody: BodyUUID
): BodyUUID[] {
  const path: BodyUUID[] = [];
  let current: BodyUUID | null = endEffectorBody;
  while (current) {
    path.push(current);
    if (current === rootBody) break;
    current = parentMap[current] ?? null;
  }
  return path.reverse();
}

/**
 * Build a simple IK chain from the scene graph.
 * - Includes only scalar joints (hinge/slide) along the path rootBody -> endEffectorBody.
 * - Uses body.position/orientation as the fixed parent->child offsets.
 */
export function buildIkChainFromScene(
  scene: SceneLike,
  endEffectorBody: BodyUUID,
  rootBody?: BodyUUID
): IkChain | null {
  lastIkChainBuildFailureReason = "";
  const parentMap = buildBodyParentMap(scene.bodies);
  const firstWorldBody = scene.worldBodies?.[0] as BodyUUID | { uuid?: BodyUUID } | undefined;
  const resolvedRoot =
    rootBody || (typeof firstWorldBody === "string" ? firstWorldBody : firstWorldBody?.uuid);
  if (!resolvedRoot) {
    return failIkChainBuild("No root body available to build chain");
  }

  if (!scene.bodies[endEffectorBody]) {
    return failIkChainBuild("End-effector body not found in scene");
  }

  const path = collectPath(parentMap, endEffectorBody, resolvedRoot);
  if (path[path.length - 1] !== endEffectorBody) {
    return failIkChainBuild("End-effector body is disconnected from the IK root");
  }

  const links: IkLink[] = [];
  let dofIndex = 0;

  for (let i = 1; i < path.length; i++) {
    const bodyUuid = path[i];
    const parentUuid = path[i - 1];
    const body = scene.bodies[bodyUuid];
    const parentBody = scene.bodies[parentUuid];
    if (!body || !parentBody) continue;

    const jointIds = Array.isArray(body.joints) ? body.joints : [];
    const scalarJointUuids: JointUUID[] = [];
    jointIds.forEach((jointRef) => {
      const jointUuid = jointRef as JointUUID | undefined;
      if (!jointUuid) return;
      const joint = scene.joints[jointUuid];
      if (!isScalarIkJoint(joint)) return;
      scalarJointUuids.push(jointUuid);
    });

    if (scalarJointUuids.length === 0) {
      // Fixed transform segment (no joint): still affects FK and thus IK targeting.
      links.push({
        kind: "fixed",
        bodyUuid,
        parentBodyUuid: parentUuid,
        axis: [0, 0, 1],
        anchor: [0, 0, 0],
        bodyOffsetPosition: toVector3Tuple(body.position, [0, 0, 0] as Vector3),
        bodyOffsetOrientation: toQuaternionTuple(body.orientation, [1, 0, 0, 0] as Quaternion),
      });
      continue;
    }

    scalarJointUuids.forEach((jointUuid) => {
      const joint = scene.joints[jointUuid];
      if (!isScalarIkJoint(joint)) return;

      const link: IkLink = {
        jointUuid,
        dofIndex,
        bodyUuid,
        parentBodyUuid: parentUuid,
        kind: joint.kind,
        axis: toVector3Tuple(joint.axis, [0, 0, 1] as Vector3),
        anchor: toVector3Tuple(joint.position, [0, 0, 0] as Vector3),
        limit: Array.isArray(joint.range) ? joint.range : undefined,
        reference: Number.isFinite(Number(joint.initialRef)) ? Number(joint.initialRef) : joint.ref,
        bodyOffsetPosition: toVector3Tuple(body.position, [0, 0, 0] as Vector3),
        bodyOffsetOrientation: toQuaternionTuple(body.orientation, [1, 0, 0, 0] as Quaternion),
      };

      links.push(link);
      dofIndex += 1;
    });
  }

  if (!links.length || dofIndex === 0) {
    return failIkChainBuild("No hinge/slide joints found along path for IK chain");
  }

  const rootBodyPose = scene.bodies[resolvedRoot];
  const rootTransform = {
    position: toVector3Tuple(rootBodyPose?.position, [0, 0, 0] as Vector3),
    orientation: toQuaternionTuple(rootBodyPose?.orientation, [1, 0, 0, 0] as Quaternion),
  };

  return {
    links,
    dofCount: dofIndex,
    endEffectorBody,
    rootBody: resolvedRoot,
    rootTransform,
  };
}
